import { StepLayout } from "#/components/carrot-plans/shared/StepLayout"
import {
  ButtonFooterLayout,
  ContinueButton,
  RoutingFlowTitle,
  SkipButton,
  useConvertTranslatedLabel
} from "#/components/carrot-plans/shared"
import React from "react"
import { Form } from "@carrotfertility/carotene-core-x"
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { FormRadioButtonGroup } from "#/services/common-forms"
import { useGetMedicalInfo, useMedicalInfo } from "#/components/carrot-plans/context/MedicalInfoContext"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"

type ReturnToWorkFieldName = "pregnancyReturnToWorkPlan" | "postpartumReturnToWorkStatus"

function ReturnToWorkStep({
  fieldName,
  headerText,
  options
}: {
  fieldName: ReturnToWorkFieldName
  headerText: MessageDescriptor
  options: { label: MessageDescriptor; value: string }[]
}): JSX.Element {
  const intl = useIntl()
  const { updateMedicalInfo, hasQuestionBeenSkipped } = useMedicalInfo()
  const medicalInfoQuery = useGetMedicalInfo()
  const defaultValue = medicalInfoQuery.data?.[fieldName]
  const descriptionText = intl.formatMessage({
    defaultMessage:
      "This information helps us provide guidance and suggest relevant resources, and is never shared with your benefit sponsor."
  })

  const { send } = useStateMachine(defaultValue || hasQuestionBeenSkipped(fieldName) ? "" : null)

  async function onClickContinue(formValues: Record<string, string>): Promise<void> {
    const value = formValues[fieldName]
    if (value != null) {
      await updateMedicalInfo({ [fieldName]: value })
      send("")
    } else {
      await onSkip()
    }
  }

  async function onSkip(): Promise<void> {
    await updateMedicalInfo({ skipFields: [fieldName] })
    send("")
  }

  const radioOptions = useConvertTranslatedLabel(options)
  const fieldLabelId = `${fieldName}-label`

  return (
    <>
      <RoutingFlowTitle title={intl.formatMessage(headerText)} />
      <StepLayout
        header={<span id={fieldLabelId}>{intl.formatMessage(headerText)}</span>}
        description={descriptionText}
        formElements={
          <Form
            defaultValues={{
              [fieldName]: defaultValue || ""
            }}
            onSubmit={onClickContinue}
          >
            <FormRadioButtonGroup name={fieldName} aria-labelledby={fieldLabelId} options={radioOptions} />
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
              skipButton={<SkipButton onClick={onSkip} />}
            />
          </Form>
        }
      />
    </>
  )
}

const pregnancyReturnToWorkHeader = defineMessage({
  defaultMessage:
    "If you are currently employed part or full time, do you intend to return to work after birth/parental leave? "
})

const pregnancyReturnToWorkOptions = [
  { label: defineMessage({ defaultMessage: "Yes" }), value: "WILL_RETURN_TO_WORK" },
  { label: defineMessage({ defaultMessage: "No" }), value: "WILL_NOT_RETURN_TO_WORK" },
  { label: defineMessage({ defaultMessage: "I'm not currently employed" }), value: "NOT_CURRENTLY_EMPLOYED" },
  { label: defineMessage({ defaultMessage: "I don't know yet" }), value: "NOT_SURE" }
]

export const PregnantReturnToWorkStep = (): JSX.Element => (
  <ReturnToWorkStep
    fieldName="pregnancyReturnToWorkPlan"
    headerText={pregnancyReturnToWorkHeader}
    options={pregnancyReturnToWorkOptions}
  />
)

const postpartumReturnToWorkHeader = defineMessage({
  defaultMessage: "What are your parental leave and return-to-work plans?"
})

const postpartumReturnToWorkOptions = [
  {
    label: defineMessage({ defaultMessage: "I’m on leave and plan to return to work" }),
    value: "ON_LEAVE_PLAN_TO_RETURN"
  },
  {
    label: defineMessage({ defaultMessage: "I’m on leave and do not plan to return to work" }),
    value: "ON_LEAVE_DO_NOT_PLAN_TO_RETURN"
  },
  {
    label: defineMessage({ defaultMessage: "I already took leave and have returned to work" }),
    value: "TOOK_LEAVE_HAVE_RETURNED"
  },
  {
    label: defineMessage({ defaultMessage: "I already took leave and did not return to work" }),
    value: "TOOK_LEAVE_DID_NOT_RETURN"
  },
  { label: defineMessage({ defaultMessage: "I did not take leave" }), value: "DID_NOT_TAKE_LEAVE" },
  { label: defineMessage({ defaultMessage: "I'm not currently employed" }), value: "NOT_CURRENTLY_EMPLOYED" },
  { label: defineMessage({ defaultMessage: "I haven’t decided yet" }), value: "HAVE_NOT_DECIDED" }
]

export const PostpartumReturnToWorkStep = (): JSX.Element => (
  <ReturnToWorkStep
    fieldName="postpartumReturnToWorkStatus"
    headerText={postpartumReturnToWorkHeader}
    options={postpartumReturnToWorkOptions}
  />
)
